/**
 * 头像与语音面板：预设 emoji 网格 + 上传自定义图片，二选一；最下面选短语语音的声音。
 *
 * 点了就生效，没有「确定」按钮：和 `NicknameRow` 一样，改头像是个低风险、
 * 随时能改回来的动作，多一次确认只是多一次点击。上传除外——那是一次真实的
 * 网络请求 + 落盘，点了立刻显示「上传中」，失败要看得见原因。
 */
import { useCallback, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { DEFAULT_VOICE, type PublicAccount, type VoiceId } from '@poker/shared'

import { IconAlert } from './lobbyIcons.js'
import { prepareAvatarUpload } from './avatarUpload.js'
import { ProfileError, removeCustomAvatar, resetAvatarToDefault, saveVoice, setEmojiAvatar, uploadCustomAvatar } from './profileApi.js'
import { playPhraseVoice } from '../fx/sampleSfx.js'
import { ownAvatarVersion, resolveAvatar } from '../table/avatar.js'
import { SheetShell } from '../auth/sheetShell.js'
import '../auth/account.css'
import './avatarPicker.css'

/**
 * 预设的常见有趣 emoji。都是主流平台渲染一致、辨识度高的字符——
 * 特意避开需要 ZWJ 拼接的复合表情（不同系统拼图差异大，容易显示成方块）。
 */
export const PRESET_AVATAR_EMOJI: readonly string[] = [
  '😎', '🤠', '🥸', '🤡', '👻', '🤖', '👽', '💀',
  '🐶', '🐱', '🦊', '🐼', '🦁', '🐸', '🐵', '🦄',
  '🐷', '🐔', '🦉', '🐢', '🐙', '🦀', '🐳', '🦖',
  '🃏', '🎲', '🎰', '🀄', '💰', '👑', '🔥', '⚡',
]

/** 短语语音的两个选项。文案只说声音，不问性别 */
export const VOICE_OPTIONS: readonly { readonly id: VoiceId; readonly label: string }[] = [
  { id: 'male', label: '男声' },
  { id: 'female', label: '女声' },
]

export interface AvatarPickerSheetProps {
  readonly account: PublicAccount | null
  onClose?(): void
  /** 注入点：测试用；默认打真接口 */
  readonly setEmoji?: (char: string) => Promise<PublicAccount>
  readonly resetDefault?: () => Promise<PublicAccount>
  readonly upload?: (dataUrl: string) => Promise<PublicAccount>
  readonly removeCustom?: () => Promise<PublicAccount>
  readonly setVoice?: (voice: VoiceId) => Promise<PublicAccount>
  /** 选中后试播一句。注入点：测试用 */
  readonly previewVoice?: (voice: VoiceId) => void
  /** 注入点：测试用，替换真实的文件读取/压缩管线 */
  readonly prepareUpload?: (file: File) => Promise<string>
}

export function AvatarPickerSheet({
  account,
  onClose,
  setEmoji,
  resetDefault,
  upload,
  removeCustom,
  setVoice,
  previewVoice,
  prepareUpload,
}: AvatarPickerSheetProps): ReactNode {
  const [error, setError] = useState<string | null>(null)
  /** 刚点的声音：请求回来之前先按它显示，失败再退回账号上的 */
  const [pendingVoice, setPendingVoice] = useState<VoiceId | null>(null)
  const [busy, setBusy] = useState<'idle' | 'saving' | 'uploading'>('idle')
  const fileInput = useRef<HTMLInputElement | null>(null)

  const avatarSeed = account?.avatarSeed ?? ''
  const accountId = account?.id ?? ''
  // 第三个参数只对「我自己」的头像有意义：重新上传后地址不变，得靠它把 <img> 顶开
  const current = resolveAvatar(avatarSeed, accountId, ownAvatarVersion())

  const run = useCallback((busyKind: 'saving' | 'uploading', task: () => Promise<PublicAccount>): void => {
    setBusy(busyKind)
    setError(null)
    void task().then(
      () => setBusy('idle'),
      (e: unknown) => {
        setError(e instanceof ProfileError || e instanceof Error ? e.message : '没能保存，再试一次')
        setBusy('idle')
      },
    )
  }, [])

  const voice = pendingVoice ?? account?.voice ?? DEFAULT_VOICE

  const pickVoice = useCallback(
    (next: VoiceId): void => {
      // 点了就试播一句：这一下是手势，iOS 上也放得出声。试听是用户主动点的，不看牌桌音效开关
      // （大厅里也找不到那个开关，关着的话用户只会以为坏了）
      ;(previewVoice ?? ((v: VoiceId) => playPhraseVoice('hurry', v, true, { fromGesture: true })))(next)
      if (next === (account?.voice ?? DEFAULT_VOICE)) {
        setPendingVoice(null)
        return
      }
      setPendingVoice(next)
      setBusy('saving')
      setError(null)
      void (setVoice ?? saveVoice)(next).then(
        () => {
          setPendingVoice(null)
          setBusy('idle')
        },
        (e: unknown) => {
          setPendingVoice(null)
          setError(e instanceof ProfileError || e instanceof Error ? e.message : '没能保存，再试一次')
          setBusy('idle')
        },
      )
    },
    [account?.voice, previewVoice, setVoice],
  )

  const pickEmoji = useCallback(
    (char: string): void => {
      run('saving', () => (setEmoji ?? setEmojiAvatar)(char))
    },
    [setEmoji, run],
  )

  const onFileChange = useCallback(
    (file: File | undefined): void => {
      if (file === undefined) return
      setBusy('uploading')
      setError(null)
      const prep = prepareUpload ?? prepareAvatarUpload
      void prep(file)
        .then((dataUrl) => (upload ?? uploadCustomAvatar)(dataUrl))
        .then(
          () => setBusy('idle'),
          (e: unknown) => {
            setError(e instanceof Error ? e.message : '没能上传，再试一次')
            setBusy('idle')
          },
        )
    },
    [prepareUpload, upload],
  )

  return (
    <SheetShell title="头像与语音" dismissible={busy === 'idle'} {...(onClose === undefined ? {} : { onClose })}>
      {error !== null && (
        <div className="lobby-error" role="alert">
          <IconAlert size={18} />
          <span>{error}</span>
        </div>
      )}

      <div className="avatar-preview" aria-hidden="true">
        <span className="avatar-preview__circle" data-kind={current.kind}>
          {current.kind === 'custom' ? (
            <img className="avatar-preview__img" src={current.url} alt="" width={88} height={88} />
          ) : current.kind === 'emoji' ? (
            current.char
          ) : (
            '?'
          )}
        </span>
      </div>

      <div className="avatar-actions">
        <input
          ref={fileInput}
          type="file"
          name="avatar"
          aria-label="上传头像照片"
          tabIndex={-1}
          accept="image/png,image/jpeg,image/webp"
          className="avatar-file-input"
          disabled={busy !== 'idle'}
          onChange={(e) => {
            const file = e.target.files?.[0]
            onFileChange(file)
            e.target.value = ''
          }}
        />
        <button
          type="button"
          className="lobby-btn"
          aria-live="polite"
          disabled={busy !== 'idle'}
          onClick={() => fileInput.current?.click()}
        >
          {busy === 'uploading' ? '正在上传…' : '上传照片'}
        </button>
        {avatarSeed !== '' && (
          <button
            type="button"
            className="lobby-btn lobby-btn--ghost"
            disabled={busy !== 'idle'}
            onClick={() =>
              run('saving', () => (current.kind === 'custom' ? (removeCustom ?? removeCustomAvatar)() : (resetDefault ?? resetAvatarToDefault)()))
            }
          >
            恢复默认头像
          </button>
        )}
      </div>

      <p className="acct-say">或者选一个：</p>
      <div className="avatar-grid" role="group" aria-label="预设头像">
        {PRESET_AVATAR_EMOJI.map((char) => (
          <button
            key={char}
            type="button"
            className="avatar-grid__item"
            disabled={busy !== 'idle'}
            aria-pressed={current.kind === 'emoji' && current.char === char}
            aria-label={`用 ${char} 当头像`}
            onClick={() => pickEmoji(char)}
          >
            {char}
          </button>
        ))}
      </div>

      <div className="avatar-voice">
        <span className="avatar-voice__label" id="avatar-voice-label">
          短语语音
        </span>
        <div className="avatar-voice__opts" role="group" aria-labelledby="avatar-voice-label">
          {VOICE_OPTIONS.map((o) => (
            <button
              key={o.id}
              type="button"
              className="avatar-voice__opt"
              aria-pressed={voice === o.id}
              disabled={busy !== 'idle'}
              onClick={() => pickVoice(o.id)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </SheetShell>
  )
}
